/* eslint-disable import/no-restricted-paths */
import { Minus, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { FeedbackToolProvider } from "@/features/request/feedback-tool/feedbackTool/components/FeedbackToolProvider";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { useImagePreview } from "@/hooks/useImagePreview";

import type { UseVideoConversionOptions } from "./hooks/useVideoConversion";

import PdfViewer from "../pdf-viewer";
import { useVideoConversion } from "./hooks/useVideoConversion";

const VIDEO_FORMATS = ["avi", "mov", "mkv", "mp4", "m4v"];
const IMAGE_FORMATS = ["png", "bmp", "webp", "jpg", "jpeg", "svg", "gif"];
const PDF_FORMATS = ["pdf"];

const getMediaType = (url: string) => {
  if (VIDEO_FORMATS.some((format) => url.toLowerCase().includes(format))) {
    return "VIDEO";
  }

  if (IMAGE_FORMATS.some((format) => url.toLowerCase().includes(format))) {
    return "IMAGE";
  }

  if (PDF_FORMATS.some((format) => url.toLowerCase().includes(format))) {
    return "PDF";
  }

  return "UNKNOWN";
};

interface MediaViewerProps {
  alt?: string;
  attachmentId: number;
  className?: string;
  conversionPollOptions?: UseVideoConversionOptions;
  extension: string;
  showFullscreenToggle?: boolean;
  showZoomControls?: boolean;
  src: string;
  usePreview?: boolean;
}

export function MediaViewer({
  alt = "",
  attachmentId,
  className = "",
  conversionPollOptions,
  extension,
  showFullscreenToggle = true,
  showZoomControls = true,
  src,
  usePreview = false,
}: MediaViewerProps) {
  const [size, _setSize] = useState<"FULL" | "MODAL">("FULL");
  const [zoom, setZoom] = useState(1);
  const [controls, setControls] = useState(150);

  useEffect(() => {
    setZoom(1);
    setControls(150);
  }, [src]);

  const mediaType = getMediaType(extension);

  const applyZoom = (type: "ZOOM_IN" | "ZOOM_OUT") => {
    if (type === "ZOOM_OUT") {
      if (controls >= 150) return;
      setControls((prev) => prev + 15);
      setZoom(zoom * 0.8);
    } else {
      if (controls <= 60) return;
      setControls((prev) => prev - 15);
      setZoom(zoom * 1.25);
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (mediaType !== "IMAGE" || size !== "FULL") return;

    e.preventDefault();

    if (e.deltaY > 0) {
      applyZoom("ZOOM_OUT");
    } else {
      applyZoom("ZOOM_IN");
    }
  };

  return (
    <div
      className={`${className} ${mediaType === "PDF" ? "w-full h-full" : ""}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Zoom Controls */}
      {(showZoomControls || showFullscreenToggle) && (
        <div
          className={`absolute ${size === "MODAL" ? "bottom-8 right-8" : "bottom-25 right-25"} z-20 flex items-center gap-4`}
        >
          {showZoomControls && size === "FULL" && mediaType === "IMAGE" && (
            <div className="flex items-center">
              <Minus
                className="text-white cursor-pointer mr-4 drop-shadow-lg"
                onClick={() => applyZoom("ZOOM_OUT")}
                size={24}
              />
              <div
                className="bg-white border-4 border-[#6938EF] h-6 w-6 rounded-full cursor-pointer absolute transition-all duration-300"
                style={{ right: controls }}
              />
              <div className="bg-white/70 h-3 w-30 rounded-sm" />
              <Plus
                className="text-white cursor-pointer ml-4 mr-4 drop-shadow-lg"
                onClick={() => applyZoom("ZOOM_IN")}
                size={24}
              />
            </div>
          )}
        </div>
      )}

      {/* Media Content */}
      <div className="w-full h-full flex justify-center items-center">
        {mediaType === "IMAGE" && (
          <ImageViewer
            alt={alt}
            key={src}
            onWheel={handleWheel}
            size={size}
            src={src}
            usePreview={usePreview}
            zoom={zoom}
          />
        )}
        {mediaType === "VIDEO" && (
          <VideoViewer
            attachmentId={attachmentId}
            conversionPollOptions={conversionPollOptions}
            extension={extension}
            size={size}
            src={src}
          />
        )}
        {mediaType === "PDF" && (
          <PDFViewer attachmentId={attachmentId} url={src} />
        )}
        {mediaType === "UNKNOWN" && (
          <div className="flex items-center justify-center p-8">
            <p className="text-gray-500">Unsupported media type</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Image Viewer Component
function ImageViewer({
  alt,
  onWheel,
  size,
  src,
  usePreview,
  zoom,
}: {
  alt: string;
  onWheel?: (e: React.WheelEvent) => void;
  size: "FULL" | "MODAL";
  src: string;
  usePreview?: boolean;
  zoom: number;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const { onError, src: activeSrc } = useImagePreview({
    preferredVariant: usePreview ? "preview" : "original",
    url: src,
  });

  return (
    <div className="relative bg-gray-200 max-h-150 max-w-150" onWheel={onWheel}>
      <img
        alt={alt}
        className="max-w-full max-h-[70vh] object-contain"
        onError={onError}
        ref={imgRef}
        src={activeSrc}
        style={{
          transform: size === "FULL" ? `scale(${zoom})` : undefined,
          transformOrigin: "center center",
        }}
      />
    </div>
  );
}

// Video Viewer Component
function VideoViewer({
  attachmentId,
  conversionPollOptions,
  extension,
  size,
  src,
}: {
  attachmentId: number;
  conversionPollOptions?: UseVideoConversionOptions;
  extension: string;
  size: "FULL" | "MODAL";
  src: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const needsConversion =
    extension.toLowerCase() === "mov" || !/^https?:\/\//i.test(src);
  const { convertedUrl, status } = useVideoConversion(
    attachmentId,
    needsConversion,
    conversionPollOptions,
  );
  const playbackSrc = needsConversion ? (convertedUrl ?? src) : src;

  useEffect(() => {
    videoRef.current?.load();
  }, [playbackSrc]);

  if (needsConversion && status === "converting") {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-gray-500">Preparing preview…</p>
      </div>
    );
  }

  if (needsConversion && status === "unavailable") {
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-8">
        <p className="text-gray-500">
          Preview isn&apos;t available for this file.
        </p>
        <a className="text-blue-600 underline" download href={src}>
          Download to view
        </a>
      </div>
    );
  }

  return (
    <div
      className={`relative flex justify-center items-center ${size === "MODAL" ? "min-h-150 min-w-150" : "max-h-full max-w-full"}`}
    >
      <video
        className="max-w-full max-h-full object-contain"
        controls
        ref={videoRef}
        src={playbackSrc}
        style={{ maxHeight: 600 }}
      >
        <source src={playbackSrc} type="video/mp4" />
        Your browser doesn&apos;t support the video tag.
      </video>
    </div>
  );
}

// PDF Viewer Component
function PDFViewer({
  attachmentId,
  url,
}: {
  attachmentId: number;
  url: string;
}) {
  return (
    <FeedbackToolProvider>
      <PDFViewerBody attachmentId={attachmentId} url={url} />
    </FeedbackToolProvider>
  );
}

function PDFViewerBody({
  attachmentId,
  url,
}: {
  attachmentId: number;
  url: string;
}) {
  const { isTablet } = useFeedbackTool();

  return isTablet ? (
    <div className="h-full w-full relative">
      <PdfViewer attachmentId={attachmentId} url={url} />
    </div>
  ) : (
    <div className="h-screen relative flex justify-center items-center">
      <div className="h-screen overflow-y-auto mt-38">
        <PdfViewer attachmentId={attachmentId} url={url} />
      </div>
    </div>
  );
}
